import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import getApiBase from './getApiBase';

/**
 * ProtectedRoute component that checks if user has required role
 * @param {Object} props
 * @param {React.ReactNode} props.children - Component to render if authorized
 * @param {boolean} props.requireDeveloper - If true, requires developer role (super_admin, admin, or developer)
 * @param {boolean} props.requireAdmin - If true, requires any admin role
 */
const ProtectedRoute = ({ children, requireDeveloper = false, requireAdmin = false }) => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthorization = async () => {
      try {
        // First check if user is authenticated
        const userResponse = await fetch(`${getApiBase()}/api/auth/me`, {
          credentials: 'include'
        });

        if (!userResponse.ok) {
          navigate('/login');
          return;
        }

        const userData = await userResponse.json();
        const userEmail = userData.user?.email;

        if (!userEmail) {
          navigate('/login');
          return;
        }

        // Get admin roles to check permissions
        const rolesResponse = await fetch(`${getApiBase()}/api/admin-management/roles`, {
          credentials: 'include'
        });

        if (!rolesResponse.ok) {
          // Not an admin - redirect
          navigate('/admin');
          return;
        }

        const rolesData = await rolesResponse.json();
        const currentUser = rolesData.admins?.find((admin) => admin.email === userEmail);

        if (!currentUser || !currentUser.admin_roles) {
          // No admin roles found
          navigate('/admin');
          return;
        }

        const activeRoles = currentUser.admin_roles.filter((role) => role.is_active !== false);

        // Check developer requirement
        if (requireDeveloper) {
          // Check if Developer is in primary OR secondary role
          const hasDeveloperInPrimary = activeRoles.some(
            (role) => role.role_type === 'developer' && role.primary_role === true
          );
          const hasDeveloperInSecondary = activeRoles.some(
            (role) => role.role_type === 'developer' && (role.primary_role === false || !role.primary_role)
          );
          
          // Also allow super_admin and admin roles (they have developer access)
          const hasSuperAdminOrAdmin = activeRoles.some(
            (role) => ['super_admin', 'admin'].includes(role.role_type)
          );
          
          const isDeveloper = hasDeveloperInPrimary || hasDeveloperInSecondary || hasSuperAdminOrAdmin;

          if (!isDeveloper) {
            // Not a developer - redirect to admin dashboard
            navigate('/admin');
            return;
          }
        }

        // Check admin requirement
        if (requireAdmin) {
          if (activeRoles.length === 0) {
            navigate('/admin');
            return;
          }
        }

        // Authorized
        setIsAuthorized(true);
      } catch (error) {
        console.error('Error checking authorization:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuthorization();
  }, [navigate, requireDeveloper, requireAdmin]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Will redirect in useEffect
  }

  return <>{children}</>;
};

export default ProtectedRoute;

